import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { executionPlanApi } from '../services/api';
import ExecutionPlanWizard from '../components/ExecutionPlanWizard';
import ExecutionPlanDashboard from '../components/ExecutionPlanDashboard';
import Dialog from '../components/Dialog';
import { useDialog } from '../hooks/useDialog';
import Pagination from '../components/Pagination';
import './ExecutionPlans.css';

/**
 * ExecutionPlans Page
 * 
 * Main page for managing and viewing execution plans
 */
function ExecutionPlans() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreator, setShowCreator] = useState(false);
    const [activeDashboard, setActiveDashboard] = useState(null);

    // Pagination state - initialize from URL params
    const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [pageSize, setPageSize] = useState(parseInt(searchParams.get('limit')) || parseInt(localStorage.getItem('rova_limit_plans')) || 20);
    const [openMenuId, setOpenMenuId] = useState(null);
    const { dialogState, closeDialog, confirm, alert } = useDialog();

    // Update URL when pagination changes
    useEffect(() => {
        const params = new URLSearchParams(searchParams);
        params.set('page', currentPage.toString());
        params.set('limit', pageSize.toString());
        setSearchParams(params, { replace: true });
    }, [currentPage, pageSize]);

    useEffect(() => {
        loadPlans();
    }, [currentPage, pageSize]);

    const loadPlans = async () => {
        try {
            const response = await executionPlanApi.getExecutionPlans({
                page: currentPage,
                limit: pageSize
            });
            setPlans(response.data.executionPlans || []);

            // Handle pagination metadata
            if (response.data.pagination) {
                setTotalPages(response.data.pagination.totalPages);
                setTotalCount(response.data.pagination.total);
            }
        } catch (err) {
            console.error('Failed to load execution plans:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleCreatePlan = () => {
        setShowCreator(true);
    };

    const handleViewPlan = (planId) => {
        navigate(`/execution-plans/${planId}`);
    };

    const handleExecutePlan = async (plan) => {
        // For now, just show dashboard
        // In production, you'd prompt for app/device selection
        const executionId = plan.executions?.[0]?.id;
        if (executionId) {
            setActiveDashboard(executionId);
        }
    };

    const handlePlanCreated = (executionPlan) => {
        setShowCreator(false);
        loadPlans();
    };

    const handleMenuToggle = (planId, e) => {
        e.stopPropagation();
        setOpenMenuId(openMenuId === planId ? null : planId);
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (openMenuId && !event.target.closest('.execution-plans__menu-wrapper')) {
                setOpenMenuId(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [openMenuId]);

    const handleEdit = (plan, e) => {
        e.stopPropagation();
        setOpenMenuId(null);
        navigate(`/execution-plans/${plan.id}/edit`);
    };

    const handleDuplicate = async (plan, e) => {
        e.stopPropagation();
        setOpenMenuId(null);

        try {
            setLoading(true);
            const response = await executionPlanApi.duplicateExecutionPlan(plan.id);

            // Reload plans to show the new duplicate
            await loadPlans();

            // Show success message
            await alert({
                title: 'Success',
                message: `Successfully created a copy of "${plan.name}"`,
                variant: 'success'
            });
        } catch (err) {
            console.error('Failed to duplicate plan:', err);
            await alert({
                title: 'Error',
                message: 'Failed to duplicate execution plan. Please try again.',
                variant: 'danger'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (plan, e) => {
        e.stopPropagation();
        setOpenMenuId(null);

        const confirmed = await confirm({
            title: 'Delete Execution Plan',
            message: `Are you sure you want to delete "${plan.name}"? This action cannot be undone.`,
            confirmText: 'Delete',
            cancelText: 'Cancel',
            variant: 'danger'
        });

        if (!confirmed) return;

        try {
            await executionPlanApi.deleteExecutionPlan(plan.id);
            loadPlans();
        } catch (err) {
            console.error('Failed to delete plan:', err);
            await alert({
                title: 'Error',
                message: 'Failed to delete execution plan. Please try again.',
                variant: 'danger'
            });
        }
    };

    const handleExecute = (plan, e) => {
        e.stopPropagation();
        setOpenMenuId(null);
        handleExecutePlan(plan);
    };

    const getExecutionTypeBadge = (mode) => {
        const badges = {
            sequential: { label: 'Sequential', className: 'execution-plans__badge--sequential' },
            parallel: { label: 'Parallel', className: 'execution-plans__badge--parallel' }
        };
        const badge = badges[mode] || badges.sequential;
        return <span className={`execution-plans__badge ${badge.className}`}>{badge.label}</span>;
    };

    const getDeviceInfo = (plan) => {
        if (!plan.deviceProvider) return '-';

        const platformIcons = {
            android: '🤖',
            ios: '🍎',
            both: '📱'
        };

        return (
            <div className="execution-plans__device">
                {/* <span className="execution-plans__device-icon">{platformIcons[plan.platform] || '📱'}</span> */}
                <span className="execution-plans__device-text">
                    {plan.platform === 'both' ? 'iOS & Android' : plan.platform === 'ios' ? 'iOS' : 'Android'}
                </span>
            </div>
        );
    };

    const formatDuration = (ms) => {
        if (!ms) return '-';
        const seconds = Math.floor(ms / 1000);
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return minutes > 0 ? `${minutes}m ${remainingSeconds}s` : `${seconds}s`;
    };

    const formatLastRun = (date) => {
        if (!date) return '-';
        const now = new Date();
        const lastRun = new Date(date);
        const diffMs = now - lastRun;
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        if (diffDays === 0) return 'Today';
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 7) return `${diffDays} days ago`;
        if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
        return lastRun.toLocaleDateString();
    };

    if (showCreator) {
        return (
            <ExecutionPlanWizard
                onClose={() => setShowCreator(false)}
                onComplete={handlePlanCreated}
            />
        );
    }

    if (activeDashboard) {
        return (
            <ExecutionPlanDashboard
                executionId={activeDashboard}
                onClose={() => {
                    setActiveDashboard(null);
                    loadPlans();
                }}
            />
        );
    }

    return (
        <div className="execution-plans">
            <div className="execution-plans__header">
                <div className="execution-plans__header-content">
                    <h1>
                        Execution Plans
                        <span className="title-badge">{totalCount || 0}</span>
                    </h1>
                    <p>Multi-goal test execution with shared context</p>
                </div>
                <button className="execution-plans__create-btn" onClick={handleCreatePlan}>
                    + Create Execution Plan
                </button>
            </div>

            {loading ? (
                <div className="execution-plans__loading">
                    <div className="execution-plans__spinner"></div>
                    <p>Loading execution plans...</p>
                </div>
            ) : plans.length === 0 ? (
                <div className="execution-plans__empty">
                    <div className="execution-plans__empty-icon-wrapper">
                        <div className="execution-plans__empty-icon">📋</div>
                        <div className="execution-plans__empty-icon-badge">+</div>
                    </div>
                    <h2>No Execution Plans Yet</h2>
                    <p className="execution-plans__empty-description">
                        Create your first execution plan to test multiple goals with shared context and AI-powered autonomy.
                    </p>
                    <button className="execution-plans__empty-cta" onClick={handleCreatePlan}>
                        Create Your First Plan
                    </button>
                    <div className="execution-plans__features">
                        <div className="execution-plans__feature">
                            <div className="execution-plans__feature-icon">📄</div>
                            <h3>Upload Documents</h3>
                            <p>Parse PDF, DOCX, or TXT files to extract test goals automatically using AI.</p>
                        </div>
                        <div className="execution-plans__feature">
                            <div className="execution-plans__feature-icon">🤖</div>
                            <h3>AI Extraction</h3>
                            <p>AI-powered goal detection with priority, complexity, and duration estimation.</p>
                        </div>
                        <div className="execution-plans__feature">
                            <div className="execution-plans__feature-icon">🔄</div>
                            <h3>Shared Context</h3>
                            <p>Goals share state - no redundant logins or repeated actions between test cases.</p>
                        </div>
                        <div className="execution-plans__feature">
                            <div className="execution-plans__feature-icon">📊</div>
                            <h3>Real-Time Monitoring</h3>
                            <p>Watch execution progress live with deep insights and automated screen recording.</p>
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    <div className="execution-plans__table-container">
                        <table className="execution-plans__table">
                            <thead>
                                <tr>
                                    <th>PLAN NAME</th>
                                    <th>EXECUTION TYPE</th>
                                    <th>DEVICE</th>
                                    <th>GOALS</th>
                                    <th>EXECUTIONS</th>
                                    <th>AVG DURATION</th>
                                    <th>LAST RUN</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {plans.map(plan => (
                                    <tr
                                        key={plan.id}
                                        className="execution-plans__row"
                                        onClick={() => handleViewPlan(plan.id)}
                                    >
                                        <td className="execution-plans__cell-name">
                                            <div className="execution-plans__name-wrapper">
                                                {/* <span className="execution-plans__icon">📄</span> */}
                                                <div>
                                                    <div className="execution-plans__name">{plan.name}</div>
                                                    <div className="execution-plans__status">
                                                        {!plan.lastExecutedAt ? 'ACTIVE' : 'DRAFT'}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td>{getExecutionTypeBadge(plan.executionMode)}</td>
                                        <td>{getDeviceInfo(plan)}</td>
                                        <td className="execution-plans__cell-number">{plan.testGoals?.length || 0}</td>
                                        <td className="execution-plans__cell-number">{plan.totalExecutions || 0}</td>
                                        <td className="execution-plans__cell-duration">{formatDuration(plan.averageDuration)}</td>
                                        <td className="execution-plans__cell-date">{formatLastRun(plan.lastExecutedAt)}</td>
                                        <td className="execution-plans__cell-actions">
                                            <div className="execution-plans__menu-wrapper">
                                                <button
                                                    className="execution-plans__menu-btn"
                                                    onClick={(e) => handleMenuToggle(plan.id, e)}
                                                >
                                                    ⋮
                                                </button>
                                                {openMenuId === plan.id && (
                                                    <div className="execution-plans__menu-dropdown">
                                                        <Link
                                                            to={`/execution-plans/${plan.id}`}
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            View
                                                        </Link>
                                                        <Link
                                                            to={`/execution-plans/${plan.id}/edit`}
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            Edit
                                                        </Link>
                                                        <button onClick={(e) => handleDuplicate(plan, e)}>
                                                            Duplicate
                                                        </button>
                                                        {/* <button onClick={(e) => handleExecute(plan, e)}>
                                                            Execute
                                                        </button> */}
                                                        <button onClick={(e) => handleDelete(plan, e)} className="danger">
                                                            Delete
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination
                        page={currentPage}
                        totalPages={totalPages}
                        limit={pageSize}
                        onPageChange={setCurrentPage}
                        onLimitChange={setPageSize}
                        storageKey="rova_limit_plans"
                    />
                </>
            )}

            <Dialog {...dialogState} onClose={closeDialog} />
        </div>
    );
}

export default ExecutionPlans;
